<template>
  <v-dialog v-model="isOpen" max-width="800" persistent>
    <v-card class="dealer-agent-modal">
      <!-- Header -->
      <v-card-title class="modal-header">
        <div class="header-content">
          <div class="agent-info">
            <div class="agent-avatar">
              <i class="material-icons-round">support_agent</i>
            </div>
            <div class="agent-details">
              <h3 class="agent-name">Alex</h3>
              <p class="agent-title">AutoSyncX™ Representative</p>
            </div>
          </div>
          <div class="header-actions">
            <!-- Mode Toggle -->
            <v-btn-toggle v-model="currentMode" mandatory class="mode-toggle">
              <v-btn value="chat" small>
                <i class="material-icons-round">chat</i>
                Chat
              </v-btn>
              <v-btn value="voice" small>
                <i class="material-icons-round">mic</i>
                Voice
              </v-btn>
            </v-btn-toggle>
            <!-- Close Button -->
            <v-btn icon @click="closeModal" class="close-btn">
              <i class="material-icons-round">close</i>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="modal-content">
        <!-- Chat Mode -->
        <div v-if="currentMode === 'chat'" class="chat-container">
          <div class="chat-messages" ref="chatMessages">
            <div v-for="message in messages" :key="message.id" class="message" :class="message.type">
              <div class="message-content">
                <div class="message-text">{{ message.text }}</div>
                <div class="message-time">{{ formatTime(message.timestamp) }}</div>
              </div>
            </div>
            <div v-if="isTyping" class="message agent typing">
              <div class="message-content">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
          <div class="chat-input">
            <v-text-field
              v-model="newMessage"
              placeholder="Type your message..."
              outlined
              dense
              hide-details
              @keyup.enter="sendMessage"
              :disabled="isLoading"
              append-icon="send"
              @click:append="sendMessage"
            />
          </div>
        </div>

        <!-- Voice Mode -->
        <div v-if="currentMode === 'voice'" class="voice-container">
          <!-- Connection Status -->
          <v-alert
            :type="connectionStatus.type"
            :text="connectionStatus.message"
            class="mb-4"
          />

          <!-- Voice Controls -->
          <div class="text-center mb-6">
            <div class="microphone-container">
              <v-btn
                :color="isVoiceCallActive ? 'success' : 'white'"
                icon
                size="x-large"
                :class="{ 'realtime-active': isVoiceCallActive }"
                @click="toggleVoiceConversation"
                :disabled="connectionStatus.type === 'error'"
                class="microphone-button"
              >
                <i class="material-icons-round" :style="{ color: isVoiceCallActive ? 'white' : '#333' }">{{ isVoiceCallActive ? 'mic' : 'mic_off' }}</i>
              </v-btn>

              <!-- Mute Button (only show when call is active) -->
              <v-btn
                v-if="isVoiceCallActive"
                :color="isMuted ? 'red' : 'grey'"
                icon
                dark
                size="large"
                @click="toggleMute"
                class="mute-button ml-4"
              >
                <i class="material-icons-round">{{ isMuted ? 'mic_off' : 'mic' }}</i>
              </v-btn>
            </div>

            <p class="text-h6 mt-4">
              {{ isVoiceCallActive ? 'Real-time conversation active' : 'Click to start voice conversation' }}
            </p>

            <p class="text-body-2 text-grey-darken-1">
              {{ connectionStatus.type === 'success' ? 'Connected to Alex from AutoSyncX™' : 'Connecting...' }}
            </p>

            <!-- Volume Indicators -->
            <div v-if="isVoiceCallActive" class="volume-indicators mt-4">
              <div class="mb-2">
                <div class="text-caption mb-1">Input Level: {{ Math.round(currentInputVolume * 100) }}%</div>
                <v-progress-linear
                  :model-value="currentInputVolume * 100"
                  color="primary"
                  height="6"
                  rounded
                />
              </div>
              <div>
                <div class="text-caption mb-1">Output Level: {{ Math.round(currentOutputVolume * 100) }}%</div>
                <v-progress-linear
                  :model-value="currentOutputVolume * 100"
                  color="success"
                  height="6"
                  rounded
                />
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { Conversation } from '@elevenlabs/client'

export default {
  name: 'DealerAgentModal',
  props: {
    mode: {
      type: String,
      default: 'chat'
    },
    agentData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isOpen: true,
      currentMode: this.mode,
      // Chat properties
      messages: [
        {
          id: 1,
          type: 'agent',
          text: "Hi! I'm Alex from AutoSyncX™. I help dealerships expand their reach by connecting them with referral agents who bring in more buyers. How can I support your dealership today?",
          timestamp: new Date()
        }
      ],
      newMessage: '',
      isTyping: false,
      isLoading: false,
      conversationId: null,
      // Voice properties
      isVoiceCallActive: false,
      voiceLoading: false,
      isSpeaking: false,
      isListening: false,
      isMuted: false,
      canRecord: false,
      conversation: null,
      currentInputVolume: 0,
      currentOutputVolume: 0,
      signedUrl: null,
      agentId: null,
      connectionStatus: {
        type: 'info',
        message: 'Initializing voice agent connection...'
      }
    }
  },
  computed: {
    voiceIcon() {
      if (this.isSpeaking) return 'record_voice_over'
      if (this.isListening) return 'hearing'
      if (this.isVoiceCallActive) return 'phone_in_talk'
      return 'phone'
    },
    voiceStatusText() {
      if (this.isSpeaking) return 'Alex is speaking...'
      if (this.isListening) return 'Listening...'
      if (this.isVoiceCallActive) return 'Call Active'
      return 'Ready to Call'
    },
    voiceDescription() {
      if (this.isVoiceCallActive) return 'You are now connected with Alex. Speak naturally!'
      return 'Click to start a voice conversation with Alex, your AutoSyncX™ representative.'
    }
  },
  watch: {
    currentMode(newMode) {
      if (newMode === 'chat' && !this.conversationId) {
        this.initializeChat()
      }
    }
  },
  mounted() {
    if (this.currentMode === 'chat') {
      this.initializeChat()
    } else if (this.currentMode === 'voice') {
      this.initializeVoiceTesting()
    }
  },

  beforeUnmount() {
    if (this.isVoiceCallActive) {
      this.stopConversation()
    }
  },
  methods: {
    closeModal() {
      this.isOpen = false
      this.$emit('close')
    },
    
    // Chat Methods
    async initializeChat() {
      try {
        console.log('🎤 Initializing chat with dealer agent...')
        const response = await axios.post('/api/dealer/voice-agent/create-conversation')
        
        if (response.data.flag === 1) {
          this.conversationId = response.data.data.conversation_id
          console.log('✅ Chat initialized:', this.conversationId)
        }
      } catch (error) {
        console.error('❌ Error initializing chat:', error)
        this.$toast.error('Failed to initialize chat. Please try again.')
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() || this.isLoading) return

      const userMessage = {
        id: Date.now(),
        type: 'user',
        text: this.newMessage.trim(),
        timestamp: new Date()
      }

      this.messages.push(userMessage)
      this.newMessage = ''
      this.isTyping = true
      this.isLoading = true

      this.scrollToBottom()

      try {
        // Simulate API call for now
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        const agentResponse = {
          id: Date.now() + 1,
          type: 'agent',
          text: this.generateAgentResponse(),
          timestamp: new Date()
        }

        this.messages.push(agentResponse)
        this.scrollToBottom()

      } catch (error) {
        console.error('❌ Error sending message:', error)
        this.$toast.error('Failed to send message. Please try again.')
      } finally {
        this.isTyping = false
        this.isLoading = false
      }
    },

    generateAgentResponse() {
      const responses = [
        "That's a great question! AutoSyncX™ helps dealerships like yours connect with thousands of referral agents nationwide. What's your current monthly sales volume?",
        "Excellent! Our platform allows you to set your own commission rates - you have full control over pricing. How are you currently generating leads?",
        "Perfect! Most dealerships see 20-30% more qualified leads within 60 days. Would you like me to walk you through our sign-up process?",
        "Great point! Our inventory sync is simple - we can integrate via API or scraping, no complex setup required. What type of inventory management system do you use?",
        "Absolutely! We provide complete transparency with clear commission structures and no hidden fees. Would you like to schedule a quick demo to see how it works?"
      ]
      return responses[Math.floor(Math.random() * responses.length)]
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessages
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    // Voice Methods
    async initializeVoiceTesting() {
      this.connectionStatus = {
        type: 'info',
        message: 'Getting signed URL for conversation...'
      }

      try {
        const response = await axios.post('/api/dealer/voice-agent/create-conversation')

        if (response.data.flag === 1 && response.data.data) {
          this.agentId = response.data.data.agent_id
          this.signedUrl = response.data.data.signed_url

          console.log('📋 Agent ID:', this.agentId)
          console.log('📋 Signed URL:', this.signedUrl)

          this.connectionStatus = {
            type: 'success',
            message: 'Ready to start voice conversation!'
          }

          // Don't initialize microphone upfront - do it when user clicks button
          console.log('✅ Voice agent ready - microphone will be initialized when user clicks button')

        } else {
          throw new Error('Voice agent not available')
        }

      } catch (error) {
        console.error('❌ Error initializing voice:', error)
        this.connectionStatus = {
          type: 'error',
          message: 'Failed to connect to voice agent: ' + error.message
        }
      }
    },

    async initializeMicrophone() {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Browser does not support audio recording')
        }

        // Request microphone permission
        await navigator.mediaDevices.getUserMedia({ audio: true })
        this.canRecord = true

        console.log('✅ Microphone access granted')
      } catch (error) {
        console.error('❌ Failed to initialize microphone:', error)
        this.canRecord = false
        this.connectionStatus = {
          type: 'error',
          message: 'Microphone access denied. Please allow microphone access and refresh.'
        }
      }
    },

    async toggleVoiceConversation() {
      if (this.isVoiceCallActive) {
        await this.stopConversation()
      } else {
        // Ensure microphone permissions before starting
        if (!this.canRecord) {
          await this.initializeMicrophone()
        }

        if (this.canRecord) {
          await this.startConversation()
        }
      }
    },

    async startConversation() {
      try {
        if (!this.signedUrl) {
          throw new Error('No signed URL available')
        }

        this.connectionStatus = {
          type: 'info',
          message: 'Starting conversation...'
        }

        // Start ElevenLabs conversation using the official SDK
        this.conversation = await Conversation.startSession({
          signedUrl: this.signedUrl,
          onConnect: () => {
            console.log('✅ ElevenLabs conversation connected')
            this.isVoiceCallActive = true
            this.connectionStatus = {
              type: 'success',
              message: 'Real-time conversation active - speak naturally!'
            }
            this.$toast.success('Voice conversation started! You can now talk with Alex.')
          },
          onDisconnect: () => {
            console.log('🔌 ElevenLabs conversation disconnected')
            this.isVoiceCallActive = false
            this.connectionStatus = {
              type: 'info',
              message: 'Conversation ended'
            }
          },
          onError: (error) => {
            console.error('❌ ElevenLabs conversation error:', error)
            this.connectionStatus = {
              type: 'error',
              message: 'Conversation error: ' + error.message
            }
            this.isVoiceCallActive = false
            this.$toast.error('Voice conversation error occurred')
          },
          onMessage: (message) => {
            console.log('📨 Received message:', message)
            this.handleConversationMessage(message)
          },
          onUserTranscript: (transcript) => {
            console.log('🎤 User transcript:', transcript)
            this.messages.push({
              id: Date.now(),
              type: 'user',
              text: transcript,
              timestamp: new Date()
            })
          },
          onAgentResponse: (response) => {
            console.log('🤖 Agent response:', response)
            this.messages.push({
              id: Date.now() + 1,
              type: 'agent',
              text: response,
              timestamp: new Date()
            })
          }
        })

        console.log('✅ ElevenLabs conversation started successfully')

        // Start volume monitoring
        this.monitorVolumeLevels()

      } catch (error) {
        console.error('❌ Failed to start conversation:', error)
        this.connectionStatus = {
          type: 'error',
          message: 'Failed to start conversation: ' + error.message
        }
        this.isVoiceCallActive = false
        this.$toast.error('Failed to start voice conversation')
      }
    },

    async stopConversation() {
      try {
        if (this.conversation) {
          await this.conversation.endSession()
          this.conversation = null
        }

        this.isVoiceCallActive = false
        this.isMuted = false
        this.connectionStatus = {
          type: 'info',
          message: 'Conversation stopped'
        }

        console.log('✅ Conversation stopped')
        this.$toast.info('Voice conversation ended')
      } catch (error) {
        console.error('❌ Failed to stop conversation:', error)
        this.isMuted = false
      }
    },

    async toggleMute() {
      if (!this.conversation || !this.isVoiceCallActive) {
        console.warn('⚠️ No active conversation to mute')
        return
      }

      try {
        if (this.isMuted) {
          // Unmute - resume audio input
          console.log('🎤 Unmuting microphone...')
          await this.conversation.startAudioInput()
          this.isMuted = false
          this.$toast.info('Microphone unmuted')
        } else {
          // Mute - stop audio input
          console.log('🔇 Muting microphone...')
          await this.conversation.stopAudioInput()
          this.isMuted = true
          this.$toast.info('Microphone muted')
        }
      } catch (error) {
        console.error('❌ Error toggling mute:', error)
        // Fallback: try to toggle the mute state anyway
        this.isMuted = !this.isMuted
        this.$toast.info(this.isMuted ? 'Microphone muted' : 'Microphone unmuted')
      }
    },

    handleConversationMessage(message) {
      // Handle different message types based on ElevenLabs SDK structure
      if (message.type === 'user_transcript') {
        // User's speech was transcribed
        this.messages.push({
          id: Date.now(),
          type: 'user',
          text: message.user_transcript || message.transcript || 'User spoke',
          timestamp: new Date()
        })
      } else if (message.type === 'agent_response') {
        // Agent's text response
        this.messages.push({
          id: Date.now() + 1,
          type: 'agent',
          text: message.agent_response || message.response || 'Agent responded',
          timestamp: new Date()
        })
      } else {
        // Log unknown message types for debugging
        console.log('Unknown message type:', message.type, message)
      }
    },

    monitorVolumeLevels() {
      if (!this.conversation || !this.isVoiceCallActive) return

      // Monitor input and output volume levels using ElevenLabs SDK
      const updateVolumes = async () => {
        try {
          if (this.conversation) {
            this.currentInputVolume = await this.conversation.getInputVolume() || 0
            this.currentOutputVolume = await this.conversation.getOutputVolume() || 0
          }
        } catch (error) {
          // Ignore volume monitoring errors
        }

        if (this.isVoiceCallActive) {
          setTimeout(updateVolumes, 100) // Update every 100ms
        }
      }

      updateVolumes()
    }
  }
}
</script>

<style scoped>
.dealer-agent-modal {
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  padding: 20px 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-avatar {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-avatar i {
  font-size: 24px;
  color: white;
}

.agent-name {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
}

.agent-title {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mode-toggle {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
}

.modal-content {
  padding: 0;
  height: 500px;
}

/* Chat Styles */
.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8fafc;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.message.user {
  justify-content: flex-end;
}

.message.agent {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 16px;
  position: relative;
}

.message.user .message-content {
  background: #3b82f6;
  color: white;
  border-bottom-right-radius: 4px;
}

.message.agent .message-content {
  background: white;
  color: #1f2937;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-text {
  font-size: 0.95rem;
  line-height: 1.4;
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 4px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #6b7280;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

.chat-input {
  padding: 16px 20px;
  background: white;
  border-top: 1px solid #e5e7eb;
}

/* Voice Styles */
.voice-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
}

.voice-status {
  margin-bottom: 40px;
}

.voice-avatar {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  transition: all 0.3s ease;
}

.voice-avatar.speaking {
  animation: pulse-speaking 1.5s infinite;
  background: linear-gradient(135deg, #10b981, #059669);
}

.voice-avatar.listening {
  animation: pulse-listening 2s infinite;
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

@keyframes pulse-speaking {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(16, 185, 129, 0);
  }
}

@keyframes pulse-listening {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(245, 158, 11, 0);
  }
}

.voice-avatar i {
  font-size: 48px;
  color: white;
}

.voice-status-text {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.voice-description {
  font-size: 1rem;
  color: #6b7280;
  max-width: 400px;
  margin: 0 auto;
}

.voice-controls {
  display: flex;
  gap: 16px;
}

.voice-btn {
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  border-radius: 50px !important;
  text-transform: none !important;
  min-width: 180px;
}

.start-call {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  color: white !important;
}

.end-call {
  background: linear-gradient(135deg, #ef4444, #dc2626) !important;
  color: white !important;
}

/* Voice Interface Styling */
.microphone-container {
  position: relative;
  display: inline-block;
  margin: 20px 0;
}

.microphone-button {
  width: 80px !important;
  height: 80px !important;
  border-radius: 50% !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
}

.microphone-button i {
  font-size: 36px !important;
}

.realtime-active {
  animation: pulse-microphone 2s infinite;
}

@keyframes pulse-microphone {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.volume-indicators {
  max-width: 300px;
  margin: 0 auto;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    height: 400px;
  }
  
  .voice-avatar {
    width: 100px;
    height: 100px;
  }
  
  .voice-avatar i {
    font-size: 40px;
  }
  
  .voice-btn {
    min-width: 150px;
    padding: 14px 24px !important;
  }
}
</style>
